import { useState } from 'react'
import { ChevronDown, PenLine } from 'lucide-react'
import type { ChatMessage } from 'src/shared/components/SaraChat/types/chatMessage'
import type { ProductItem } from 'src/shared/components/SaraChat/message_components/ProductSelectionMessage/types'
import { StatusMessage } from 'src/shared/components/SaraChat/message_components/StatusMessage'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Button } from 'src/shared/components/ui/button'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from 'src/shared/components/ui/collapsible'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { useIsAdmin } from 'src/shared/hooks/useIsAdmin'
import { cn } from 'src/shared/components/ui/utils'
import { SaraAvatar } from './Chat/components/SaraAvatar'
import { RequestImproveResponseDialog } from './Chat/components/RequestImproveResponseDialog'
import { formatMessageTimestamp } from '../utils/chatMessageDisplay'
import {
  hasSaraMessageBody,
  parseSaraMessageContent,
  SaraMessageBody,
  SaraMessageHeader,
  saraMessageShellClass,
} from './RequestSaraMessage'
import {
  extractAssistantQuickReply,
  saraGroupFetchedLogoUrls,
} from './saraQuickActions'

type Segment =
  | { type: 'status'; messages: ChatMessage[] }
  | { type: 'message'; message: ChatMessage }

const getStatusText = (message: ChatMessage) => {
  const content = parseSaraMessageContent(message)
  const raw = typeof content === 'string' ? content : content?.message ?? ''
  return typeof raw === 'string' ? raw : String(raw ?? '')
}

/** A run of consecutive status updates collapsed into one expandable trace. */
const SaraStatusTrace = ({ messages }: { messages: ChatMessage[] }) => {
  const { t } = useLocalization()
  const [open, setOpen] = useState(false)
  const last = messages[messages.length - 1]

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="group/trace flex w-full items-center gap-2 rounded-md text-left"
        >
          <div className="min-w-0" key={last.id}>
            <StatusMessage content={{ message: getStatusText(last) }} i={0} />
          </div>
          <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
            {messages.length} {t('steps', 'steg')}
          </span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform group-data-[state=open]/trace:rotate-180" />
        </button>
      </CollapsibleTrigger>

      <CollapsibleContent>
        <Column className="mb-1 mt-1.5 gap-1.5 border-l border-violet-100 pl-3">
          {messages.map((message) => (
            <Row key={message.id} className="items-baseline gap-2">
              <span className="min-w-0 text-xs text-muted-foreground">
                {t(getStatusText(message))}
              </span>
              {message.created_at && (
                <span className="shrink-0 text-[10px] text-muted-foreground/60">
                  {formatMessageTimestamp(message.created_at)}
                </span>
              )}
            </Row>
          ))}
        </Column>
      </CollapsibleContent>
    </Collapsible>
  )
}

/**
 * Renders an uninterrupted run of Sara messages as one block with a single
 * avatar/header. Consecutive status updates collapse into an expandable
 * trace; assistant answers and tool results stay visible inline.
 */
export const RequestSaraMessageGroup = ({
  items,
  quoteId,
  onOpenProductOverlay,
  onQuickReply,
}: {
  items: Array<{ message: ChatMessage; isPrivate: boolean }>
  quoteId: number
  onOpenProductOverlay?: (
    products: ProductItem[],
    quote_id: number,
    product_categories?: string[]
  ) => void
  onQuickReply?: (phrase: string) => void
}) => {
  const { t } = useLocalization()
  const isAdmin = useIsAdmin()
  const [improveOpen, setImproveOpen] = useState(false)
  const [selectedLogoUrl, setSelectedLogoUrl] = useState<string | null>(null)
  const visibleItems = items.filter(({ message }) => hasSaraMessageBody(message))
  if (visibleItems.length === 0) return null

  const isPrivate = visibleItems.some((item) => item.isPrivate)
  const lastMessage = visibleItems[visibleItems.length - 1].message
  const assistantMessage = [...visibleItems]
    .reverse()
    .find(({ message }) => message.role === 'assistant')?.message
  const quickReply = onQuickReply
    ? extractAssistantQuickReply(visibleItems.map(({ message }) => message))
    : null
  const groupMessages = visibleItems.map((item) => item.message)
  const fetchedLogoUrls = saraGroupFetchedLogoUrls(groupMessages)
  const activeLogoUrl = selectedLogoUrl ?? fetchedLogoUrls[0] ?? null

  const segments: Segment[] = []
  visibleItems.forEach(({ message }) => {
    const lastSegment = segments[segments.length - 1]
    if (message.role === 'status') {
      if (lastSegment?.type === 'status') {
        lastSegment.messages.push(message)
        return
      }
      segments.push({ type: 'status', messages: [message] })
      return
    }
    segments.push({ type: 'message', message })
  })

  return (
    <Row className={saraMessageShellClass(isPrivate)}>
      <SaraAvatar />

      <Column className="min-w-0 flex-1 gap-0.5">
        <SaraMessageHeader
          createdAt={lastMessage.created_at}
          isPrivate={isPrivate}
        />

        <Column className="mt-1.5">
          {segments.map((segment, index) => {
            const key =
              segment.type === 'status'
                ? `status-${segment.messages[0].id}`
                : `message-${segment.message.id}`

            const content =
              segment.type === 'status' ? (
                segment.messages.length === 1 ? (
                  <SaraMessageBody
                    message={segment.messages[0]}
                    quoteId={quoteId}
                  />
                ) : (
                  <SaraStatusTrace messages={segment.messages} />
                )
              ) : (
                <SaraMessageBody
                  message={segment.message}
                  quoteId={quoteId}
                  onOpenProductOverlay={onOpenProductOverlay}
                  selectedLogoUrl={activeLogoUrl}
                  onSelectLogo={setSelectedLogoUrl}
                />
              )

            if (segments.length === 1) return <div key={key}>{content}</div>

            const isLast = index === segments.length - 1

            return (
              <Row key={key} className="gap-2.5">
                <Column className="w-2 shrink-0 items-center">
                  <span className="mt-[7px] h-2 w-2 shrink-0 rounded-full border-2 border-violet-300 bg-white" />
                  {!isLast && <span className="mt-1 w-px flex-1 bg-violet-100" />}
                </Column>
                <Column className={cn('min-w-0 flex-1', !isLast && 'pb-3.5')}>
                  {content}
                </Column>
              </Row>
            )
          })}

          {quickReply && onQuickReply && (
              <Row className="mt-2">
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={() => onQuickReply(quickReply.phrase)}
                >
                  {quickReply.label}
                </Button>
              </Row>
            )}

          {isAdmin && (
            <Row className="mt-2 flex-wrap items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => setImproveOpen(true)}
              >
                <PenLine className="mr-1.5 h-3.5 w-3.5" />
                {t('Improve this response', 'Förbättra svaret')}
              </Button>
            </Row>
          )}
        </Column>
      </Column>
      <RequestImproveResponseDialog
        open={improveOpen}
        onOpenChange={setImproveOpen}
        quoteId={quoteId}
        messageId={assistantMessage?.id}
      />
    </Row>
  )
}
